import { useRef, useState, useEffect } from "react";
import beep from '../../audios/beep1.mp3'
import shutter from '../../audios/shutter.mp3'
import { reloadPage } from '../../../utils/deferredNavigation';
const videoConstraints = {
  width: 350,
  height: 220,
  facingMode: "user",
};

const FaceCaptureStep = ({

  setScreen,
  photos,
  setPhotos,
  capturedSteps,
  setCapturedSteps,
  currentStep,
  setCurrentStep,
}) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const faceMeshRef = useRef(null);
  const cameraRef = useRef(null);
  const allCapturedRef = useRef(false);
  const capturedImagesRef = useRef({ ...photos });
  const captureInProgressRef = useRef(false);

  const [isLoading, setIsLoading] = useState(true);
  const [currentPose, setCurrentPose] = useState("Loading...");
  const [countdown, setCountdown] = useState(0);
  const [isCapturing, setIsCapturing] = useState(false);

  // Ordered sequence for capture
  const captureSequence = ["center", "left", "right", "up", "down"];

  const poses = {
    center: { name: "Looking Straight", instruction: "Look directly at the camera" },
    left: { name: "Looking Left", instruction: "Turn your head LEFT" },
    right: { name: "Looking Right", instruction: "Turn your head RIGHT" },
    up: { name: "Looking Up", instruction: "Tilt your head UP" },
    down: { name: "Looking Down", instruction: "Tilt your head DOWN" },
  };

  // Map local keys to parent photos keys
  const poseKeyMap = {
    center: "Front",
    left: "Left",
    right: "Right",
    up: "Up",
    down: "Down",
  };

  const getCurrentTargetPose = () => {
  // If a retake forced a step, use that first
  if (currentStep < captureSequence.length) {
    const poseKey = captureSequence[currentStep];
    if (!capturedImagesRef.current[poseKeyMap[poseKey]]) {
      return poseKey;
    }
  }

  // Otherwise continue normal sequence
  for (let poseKey of captureSequence) {
    if (!capturedImagesRef.current[poseKeyMap[poseKey]]) {
      return poseKey;
    }
  }

  return null; // All captured
};

useEffect(() => {
  if (countdown > 0) {
    const audio = new Audio(beep);
    audio.play().catch((err) => console.error("Beep play failed:", err));
  }
}, [countdown]);

  const getProgressPercentage = () => {
    const completedCount = Object.values(photos).filter(Boolean).length;
    return Math.round((completedCount / captureSequence.length) * 100);
  };

  const calculateHeadPose = (landmarks) => {
    if (!landmarks || landmarks.length === 0) return "center";

    const noseTip = landmarks[1];
    const leftEye = landmarks[33];
    const rightEye = landmarks[263];
    const leftMouth = landmarks[61];
    const rightMouth = landmarks[291];

    const eyeCenterX = (leftEye.x + rightEye.x) / 2;
    const horizontalOffset = noseTip.x - eyeCenterX;

    const eyeCenterY = (leftEye.y + rightEye.y) / 2;
    const mouthCenterY = (leftMouth.y + rightMouth.y) / 2;
    const verticalOffset = noseTip.y - (eyeCenterY + mouthCenterY) / 2;

    const horizontalThreshold = 0.04;
    const verticalThreshold = 0.025;

    if (Math.abs(verticalOffset) > verticalThreshold) {
      return verticalOffset < 0 ? "up" : "down";
    } else if (Math.abs(horizontalOffset) > horizontalThreshold) {
      return horizontalOffset > 0 ? "left" : "right";
    } else {
      return "center";
    }
  };

  const captureImage = async (pose) => {
    if (
      capturedImagesRef.current[poseKeyMap[pose]] ||
      isCapturing ||
      captureInProgressRef.current
    ) {
      return;
    }

    captureInProgressRef.current = true;
    setIsCapturing(true);
    setCountdown(3);

    try {
      for (let i = 3; i > 0; i--) {
        setCountdown(i);
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
          const shutterAudio = new Audio(shutter);
    shutterAudio.play().catch((err) => console.error("Shutter sound failed:", err));

      if (capturedImagesRef.current[poseKeyMap[pose]]) {
        return;
      }

      const canvas = canvasRef.current;
      const video = videoRef.current;
      const ctx = canvas.getContext("2d");

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0);

      const imageDataUrl = canvas.toDataURL("image/jpeg", 0.8);

      // Save in parent
      setPhotos((prev) => ({
        ...prev,
        [poseKeyMap[pose]]: imageDataUrl,
      }));

      capturedImagesRef.current = {
        ...capturedImagesRef.current,
        [poseKeyMap[pose]]: imageDataUrl,
      };

      setCapturedSteps((prev) => [...prev, poseKeyMap[pose]]);
      setCurrentStep((prev) => prev + 1);
    } finally {
      setCountdown(0);
      setIsCapturing(false);
      captureInProgressRef.current = false;
    }
  };

  const onResults = (results) => {
    if (results.multiFaceLandmarks && results.multiFaceLandmarks.length > 0) {
      const landmarks = results.multiFaceLandmarks[0];
      const detectedPose = calculateHeadPose(landmarks);
      const targetPose = getCurrentTargetPose();

      setCurrentPose(poses[detectedPose].name);

      if (
        targetPose &&
        detectedPose === targetPose &&
        !isCapturing &&
        !captureInProgressRef.current
      ) {
        setTimeout(() => {
          if (!captureInProgressRef.current && getCurrentTargetPose() === targetPose) {
            captureImage(targetPose);
          }
        }, 1000);
      }
    } else {
      setCurrentPose("No face detected");
    }
  };
useEffect(() => {
  capturedImagesRef.current = { ...photos };   // overwrite completely
  const allCaptured = getCurrentTargetPose() === null;
  allCapturedRef.current = allCaptured;
  if (allCaptured) {
    captureInProgressRef.current = false;
  }
}, [photos]);


useEffect(() => {
  let isCancelled = false;

  const loadFaceMesh = async () => {
    try {
      await new Promise((resolve) => {
        if (window.FaceMesh && window.Camera) {
          resolve();
        } else {
          const scripts = [
            "https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/face_mesh.js",
            "https://cdn.jsdelivr.net/npm/@mediapipe/camera_utils/camera_utils.js",
            "https://cdn.jsdelivr.net/npm/@mediapipe/control_utils/control_utils.js",
            "https://cdn.jsdelivr.net/npm/@mediapipe/drawing_utils/drawing_utils.js"
          ];
          let loaded = 0;
          scripts.forEach((src) => {
            const s = document.createElement("script");
            s.src = src;
            s.async = true;
            s.onload = () => {
              if (++loaded === scripts.length) resolve();
            };
            document.body.appendChild(s);
          });
        }
      });

      if (isCancelled) return;

      const faceMesh = new window.FaceMesh({
        locateFile: (file) =>
          `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`,
      });

      faceMesh.setOptions({
        maxNumFaces: 1,
        refineLandmarks: true,
        minDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5,
      });

      faceMesh.onResults(onResults);

      const camera = new window.Camera(videoRef.current, {
        onFrame: async () => {
          await faceMesh.send({ image: videoRef.current });
        },
        width: 640,
        height: 480,
      });

      cameraRef.current = camera;   // ✅ save camera instance
      await camera.start();
    } catch (err) {
      console.error("MediaPipe init failed–reload workaround:", err);
      // fallback; waits for a running recording to end (utils/deferredNavigation)
      reloadPage({ reason: "facecapture", subject: "Face capture needs a page refresh", verb: "Refresh to continue" });
    }
  };

  loadFaceMesh();

  return () => {
    isCancelled = true;
    if (cameraRef.current) {
      cameraRef.current.stop();   // ✅ stop camera when leaving screen
    }
  };
}, []);



  const allImagesCaptured = getCurrentTargetPose() === null;
  const currentTargetPose = getCurrentTargetPose();
  const progressPercentage = getProgressPercentage();

  const saveAllImages = () => {
    console.log("Final saved photos:", photos);
    if (cameraRef.current) {
    cameraRef.current.stop(); // Stop the camera
  }
  setScreen(3)
  };

  return (
    <div className="min-h-screen w-full bg-[#145388]  p-5 font-sans">
  <div className="max-w-6xl mx-auto bg-white/95 backdrop-blur-md rounded-3xl p-8 shadow-2xl">
    <div className="text-center mb-8">
      <h1 className="text-5xl font-extrabold text-black mb-3">
        Face Pose Capture
      </h1>

      <p className="text-lg font-medium text-slate-600">
        Automatically capture images for face recognition training
      </p>
    </div>

    <div className="flex gap-8 items-start flex-col md:flex-row">
       <div className="absolute top-5 left-5 bg-black/80 text-white p-4 rounded-xl text-sm font-semibold">
            Detected: {currentPose}
          </div>
      {/* Camera */}
      <div className="flex-1">
        <div className="relative rounded-2xl overflow-hidden bg-black shadow-2xl">
          <video ref={videoRef} className="w-full h-auto block" autoPlay muted />
          <canvas ref={canvasRef} className="hidden" />

    {countdown > 0 && (
  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-black/10 text-white w-24 h-24 rounded-full flex items-center justify-center text-4xl font-bold z-20">
    {countdown}
  </div>
)}
      

         

          {!currentTargetPose && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-emerald-600/95 text-white p-6 rounded-2xl text-2xl font-bold text-center z-10">
              All poses captured!
            </div>
          )}
        </div>
         {currentTargetPose && (
            <div className=" text-black p-6 rounded-2xl text-2xl font-bold text-center z-10">
              {poses[currentTargetPose].instruction}
            </div>
          )}
      </div>

      {/* Side Panel */}
      <div className="flex-1 bg-gradient-to-br from-gray-50 to-slate-100 rounded-2xl p-7 border">
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div className="text-lg font-semibold text-slate-700">Capture Progress</div>
            <div className="text-sm text-[#900] bg-red-100 px-4 py-1.5 rounded-full font-semibold border">
              {Object.values(photos).filter(Boolean).length} of {captureSequence.length}
            </div>
          </div>
          <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden mb-5 shadow-inner">
            <div
              className="h-full bg-gradient-to-r from-[#900] via-red-700 to-red-500 rounded-full transition-all duration-500"
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
          <div className="text-center text-xl font-bold text-slate-800">
            {progressPercentage}% Complete
          </div>
        </div>

        {currentTargetPose ? (
          <div className="bg-white rounded-2xl p-8 text-center border-2 border-[#900] mb-7">
            <div className="text-2xl font-bold text-slate-800 mb-3">
              {poses[currentTargetPose].name}
            </div>
            <div className="text-lg font-semibold text-[#900]">
              {poses[currentTargetPose].instruction}
            </div>
          </div>
        ) : (
          <div className="bg-gradient-to-br from-emerald-50 to-green-100 rounded-2xl p-8 text-center border-2 border-emerald-500 mb-7">
            <div className="text-2xl font-bold text-slate-800 mb-3">All Poses Complete!</div>
            <div className="text-lg font-semibold text-emerald-600">
              Ready to save your images
            </div>
          </div>
        )}

        {Object.values(photos).some((img) => img) && (
          <div>
            <h4 className="text-lg font-semibold text-slate-700 mb-3">Captured Images</h4>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(80px,1fr))] gap-2.5 mt-5">
              {captureSequence
                .filter((poseKey) => photos[poseKeyMap[poseKey]])
                .map((poseKey) => (
                  <div key={poseKey} className="text-center">
                    <img
                      src={photos[poseKeyMap[poseKey]]}
                      alt={`Captured ${poses[poseKey].name}`}
                      className="w-full h-20 object-cover rounded-lg border-2 border-green-500"
                    />
                    <div className="text-xs text-gray-700 mt-1 font-semibold">
                      {poses[poseKey].name}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        <div className="mt-6 text-center">
          <button
            className="bg-[#900] text-white px-8 py-4 rounded-2xl text-lg font-semibold cursor-pointer transition-all duration-300 shadow-xl disabled:opacity-50"
            onClick={saveAllImages}
            disabled={!allImagesCaptured}
          >
            {allImagesCaptured ? "Proceed to next" : `Capturing...`}
          </button>
        </div>
      </div>
    </div>

    {/* Instructions */}
    <div className="bg-gradient-to-br from-gray-50  to-slate-100 border-l-4  border-[#900] p-6 md:p-6 rounded-2xl mb-6 border border-gray-200 mt-8">
      <h3 className="text-xl font-bold text-[#900] mb-4 text-start">Step-by-Step Instructions:</h3>
      <ol className="list-decimal pl-5 space-y-2 text-base md:text-base text-start">
        <li><strong>Allow camera access</strong> when your browser prompts you</li>
        <li><strong>Position your face</strong> clearly in the camera view</li>
        <li><strong>Follow the instructions</strong> - you'll be guided through each pose one at a time</li>
        <li><strong>Hold still</strong> when you see the 3-2-1 countdown</li>
        <li><strong>Wait</strong> for all 5 poses to be captured automatically</li>
        <li><strong>Click "Save All Images"</strong> to download your photos</li>
      </ol>
      <p className="mt-4 text-sm text-gray-600">
        <strong>Tip:</strong> Make deliberate head movements - the system will guide you through each pose step by step!
      </p>
    </div>
  </div>
</div>

  );
};

export default FaceCaptureStep;
